import { useState } from 'react';
import { useSubscription } from './SubscriptionContext';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../../config/api';

const SubscriptionModal = ({ isOpen, onClose, mode = 'upgrade' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { createCheckoutSession, createPortalSession, isPro } = useSubscription();
  const { isAuthenticated } = useAuth();

  const handleUpgrade = async () => {
    if (isPro) {
      onClose();
      return;
    }

    if (!isAuthenticated) {
      // Set upgrade intent flag and trigger login flow
      sessionStorage.setItem('upgradeAfterLogin', '1');
      // Store return URL
      const returnTo = window.location.pathname + window.location.search;
      await fetch(`${API_BASE_URL}/auth/store-return`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ returnTo })
      });
      window.location.href = `${API_BASE_URL}/auth/google`;
      return;
    }

    setLoading(true);
    setError('');

    try {
      const checkoutUrl = await createCheckoutSession();
      window.location.href = checkoutUrl;
    } catch (err) {
      setError('Failed to start checkout. Please try again.');
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      const portalUrl = await createPortalSession(`${window.location.origin}/`);
      window.location.href = portalUrl;
    } catch (err) {
      setError('Failed to open subscription management. Please try again.');
      console.error('Portal error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl border border-purple-100/30">
        <div className="text-center space-y-6">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto text-purple-500">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>

          {/* Content */}
          <div className="space-y-3">
            <h3 className="text-2xl font-medium text-gray-900 font-fraunces">
              {isPro ? 'You\'re Already PRO!' : 'Upgrade to MoodShelf PRO'}
            </h3>
            
            {isPro ? (
              <p className="text-gray-600 font-space">
                You already have an active PRO subscription. Enjoy 15 recommendations per day!
              </p>
            ) : (
              <>
                <p className="text-gray-600 font-space">
                  {mode === 'upgrade' 
                    ? 'Unlock unlimited book recommendations and premium features for just $5/month!'
                    : 'Get unlimited access to all recommendations and features.'
                  }
                </p>
                
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 space-y-2">
                  <h4 className="font-semibold text-gray-900 font-space">PRO Features:</h4>
                  <ul className="text-sm text-gray-700 font-space space-y-1">
                    <li>• Unlimited similar book recommendations</li>
                    <li>• Unlimited mood-based recommendations</li>
                    <li>• No daily limits or restrictions</li>
                    <li>• Priority support</li>
                    <li>• Early access to new features</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            {isPro ? (
              <>
                <button
                  onClick={handleManageSubscription}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-space disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Manage Subscription'}
                </button>
                
                <button
                  onClick={onClose}
                  className="w-full px-6 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors font-space"
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-space disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Upgrade to PRO - $5/month'}
                </button>
                
                <button
                  onClick={onClose}
                  className="w-full px-6 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors font-space"
                >
                  Maybe later
                </button>
              </>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <p className="text-red-600 text-sm font-space">{error}</p>
          )}

          {/* Security Note */}
          {!isPro && (
            <p className="text-xs text-gray-500 font-space">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal; 